import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { LexXiModalComponent } from 'app/components/xi-components/lex-xi-modal/lex-xi-modal.component';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { Field } from 'app/providers/storage-api/field.service';
import { ModalCloseType } from '../../../../../enums';
import { WidgetDataSource } from '../../../../../models';
import {
  FieldBucket,
  FieldBucketsComponent,
  FieldBucketsFormValue,
} from '../../../../field/field-buckets/field-buckets.component';

export enum ImpactMetric {
  RATING = 'rating',
  BINARY = 'binary',
  BUCKETS = 'buckets',
}

export interface FieldSelectDialogResult {
  field: Partial<Field>;
  buckets: FieldBucket[];
  selectedBucketIndex: number;
  impactMetric: ImpactMetric;
}

@Component({
  selector: 'app-rating-field-selector-dialog',
  imports: [AppSharedModule, FieldBucketsComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './rating-field-selector-dialog.component.html',
  styleUrls: ['./rating-field-selector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatingFieldSelectorDialogComponent implements OnInit, OnDestroy {
  readonly data = input<{
    dataSource: WidgetDataSource;
    ratingFieldName: string;
    cancelButtonText: string;
    metadataFields: string[];
    customBuckets: FieldBucket[];
    selectedBucketIndex: number;
    isBucketView: boolean;
    scoringStrategy?: string;
    customBucketsByField?: {
      [fieldName: string]: { buckets: FieldBucket[]; selectedBucketIndex: number };
    };
    impactMetric?: ImpactMetric;
    resolveFn?: (result?) => any;
  }>();
  readonly title = input('select-field-impact');

  readonly modalClosed = output<FieldSelectDialogResult | ModalCloseType>();
  readonly fieldChanged = output<FieldSelectDialogResult>();
  readonly widthChange = output<string>();

  private accountService = inject(StorageAccountService);
  private customFieldService = inject(CustomFieldsService);
  private cdr = inject(ChangeDetectorRef);

  ratingFields: Partial<Field>[] = [];
  bucketFields: Partial<Field>[] = [];
  binaryFields: Partial<Field>[] = [];
  selectedField: string;
  selectedBucketField: string;
  selectedBinaryField: string;

  isLoading = true;
  fb = inject(FormBuilder);
  form: FormGroup;
  readonly IMPACT_METRIC = ImpactMetric;
  impactMetricOptions: DropdownObject[] = [];
  destroy$ = new Subject<void>();
  backButtonText: string;
  nextButtonText = translate('widgets.options.continue');
  isBucketView = false;
  bucketTitle = 'select-bucket-for-impact';
  field = signal<Partial<Field>>(null);
  fieldBuckets: FieldBucket[];
  selectedBucketIndex: number;
  areBucketsValid = true;
  isBucketsReadOnly = signal<boolean>(true);

  get isApplyDisabled(): boolean {
    if (!this.selectedBucketField) {
      return true;
    }
    const isOverride = this.form?.controls.overrideBucketSettings?.value;
    if (!isOverride) {
      return false;
    }
    return this.isBucketView && (this.selectedBucketIndex == null || !this.areBucketsValid);
  }

  get isSaveDisabled(): boolean {
    if (this.form?.controls.impactMetric.value === ImpactMetric.BINARY) {
      return !this.selectedBinaryField;
    }
    if (this.form?.controls.impactMetric.value === ImpactMetric.RATING) {
      return !this.selectedField;
    }
    return false;
  }

  constructor(private lexXiModal: LexXiModalComponent) {
    this.lexXiModal.onClose = () => {
      this.lexXiModal.close.emit(ModalCloseType.CLOSE);

      if (this.data()?.resolveFn) {
        this.data().resolveFn(ModalCloseType.CLOSE);
      }
    };
  }

  async ngOnInit(): Promise<void> {
    const data = this.data();
    this.backButtonText = data.cancelButtonText;

    this.initImpactMetricSelectOptions();
    this.initForm();
    if (data.isBucketView) {
      this.switchToBucketView();
    }
    // we need fallback if we don't have aliases
    const fieldNames = await this.customFieldService.getMetadataSubTypesAsync(data.dataSource);
    this.accountService.groupFieldsSub.takeUntil(this.destroy$).subscribe((accountMetadata) => {
      for (const field of accountMetadata.metadata) {
        if (fieldNames?.includes(field.fieldName) && field.metadata?.display !== false) {
          const mappedField = {
            fieldName: field.fieldName,
            label: field.description,
          };
          if (field.metadata.rating) {
            this.ratingFields.push(mappedField);
          }

          //Per DASH-9880 all numeric types can go to the buckets because we can
          //define them on the fly.  Also remove binary fields as they don't make sense bucketed
          if (
            (field.fieldType === 'long' ||
              field.fieldType === 'double' ||
              field.metadata?.rating) &&
            field.metadata?.calculationType !== 'binary'
          ) {
            this.bucketFields.push({
              ...mappedField,
              buckets: field.buckets,
              fieldType: field.fieldType,
            });
          }

          if (field.metadata?.calculationType === 'binary') {
            this.binaryFields.push(mappedField);
          }
        }
      }

      const sortFn = (f1, f2) =>
        f1.label.localeCompare(f2.label, undefined, { sensitivity: 'accent' });

      if (this.ratingFields.length) {
        this.ratingFields.sort(sortFn);
      } else {
        this.impactMetricOptions.find((o) => o.id === ImpactMetric.RATING).disabled = true;
      }

      if (this.bucketFields?.length > 0) {
        this.bucketFields.sort(sortFn);
      } else {
        this.impactMetricOptions.find((o) => o.id === ImpactMetric.BUCKETS).disabled = true;
      }
      if (this.binaryFields?.length > 0) {
        this.binaryFields.sort(sortFn);
      } else {
        this.impactMetricOptions.find((o) => o.id === ImpactMetric.BINARY).disabled = true;
      }

      this.selectedField = data.ratingFieldName;
      if (!this.selectedField) {
        this.selectedField = this.ratingFields[0]?.fieldName;
      }

      // Determine the impact metric: use stored value or infer from field arrays
      const impactMetric = data.impactMetric ?? this.inferImpactMetric();

      // Initialize the form based on the determined impact metric
      this.initializeImpactMetric(impactMetric);
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    const data = this.data();
    // Check if there are any custom buckets (either direct or in customBucketsByField)
    const hasCustomBuckets =
      !!data.customBuckets ||
      (data.customBucketsByField &&
        Object.values(data.customBucketsByField).some((d) => d?.buckets?.length > 0));

    this.form = this.fb.group({
      impactMetric: this.fb.control<ImpactMetric>(ImpactMetric.RATING),
      overrideBucketSettings: this.fb.control<boolean>(hasCustomBuckets),
    });

    // Initialize the read-only signal
    this.isBucketsReadOnly.set(!hasCustomBuckets);

    this.form.controls.impactMetric.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((metric: ImpactMetric) => {
        switch (metric) {
          case ImpactMetric.RATING:
            if (!this.selectedField) {
              this.selectedField = this.ratingFields[0]?.fieldName;
            }
            break;
          case ImpactMetric.BINARY:
            if (!this.selectedBinaryField) {
              this.selectedBinaryField = this.binaryFields[0]?.fieldName;
              if (this.selectedBinaryField) {
                this.onBinaryFieldChange(this.selectedBinaryField);
              }
            }
            break;
          case ImpactMetric.BUCKETS:
            this.selectedBucketIndex = undefined;
            this.nextButtonText = translate('widgets.options.continue');
            if (!this.selectedBucketField) {
              this.selectedBucketField = this.bucketFields[0]?.fieldName;
              if (this.selectedBucketField) {
                this.onBucketFieldChange(this.selectedBucketField);
              }
            }
            break;
        }
      });

    this.form.controls.overrideBucketSettings.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((override) => {
        this.isBucketsReadOnly.set(!override);
        const field = this.bucketFields.find((f) => f.fieldName === this.selectedBucketField);
        const storedBucketData = this.data()?.customBucketsByField?.[this.selectedBucketField];

        // Determine which buckets to use
        const bucketsToUse = override && storedBucketData?.buckets ? storedBucketData.buckets : [];

        this.field.set({
          ...field,
          buckets: override ? bucketsToUse : field.buckets,
        });

        if (!override) {
          // When unchecking override, if buckets exist (original or stored), ensure selectedBucketIndex is set
          if (bucketsToUse.length && !this.selectedBucketIndex) {
            this.selectedBucketIndex = 0;
            return;
          }
          // except when we are not overriding and there are no buckets to use
          if (!bucketsToUse.length) {
            this.selectedBucketIndex = undefined;
            return;
          }
        }
        // and if we override and we have stored buckets to use we should preselect the stored index
        if (bucketsToUse.length) {
          this.selectedBucketIndex = storedBucketData.selectedBucketIndex;
        }
      });
  }

  private initImpactMetricSelectOptions() {
    this.impactMetricOptions = Object.keys(ImpactMetric).map((key) => ({
      title: translate('widgets.options.' + ImpactMetric[key]),
      disabled: false,
      id: ImpactMetric[key],
    }));
  }

  private inferImpactMetric(): ImpactMetric {
    const data = this.data();
    // Infer the impact metric based on which array contains the current field
    const binaryIndex = this.binaryFields.findIndex((f) => f.fieldName === data.ratingFieldName);
    const bucketIndex = this.bucketFields.findIndex((f) => f.fieldName === data.ratingFieldName);
    const ratingIndex = this.ratingFields.findIndex((f) => f.fieldName === data.ratingFieldName);

    if (binaryIndex !== -1) {
      return ImpactMetric.BINARY;
    } else if (data.customBuckets && bucketIndex !== -1) {
      return ImpactMetric.BUCKETS;
    } else if (bucketIndex !== -1 && ratingIndex === -1) {
      return ImpactMetric.BUCKETS;
    }

    return ImpactMetric.RATING;
  }

  private initializeImpactMetric(impactMetric: ImpactMetric): void {
    const data = this.data();
    this.form.controls.impactMetric.setValue(impactMetric);

    switch (impactMetric) {
      case ImpactMetric.BINARY:
        this.selectedBinaryField = data.ratingFieldName;
        this.onBinaryFieldChange(this.selectedBinaryField);
        break;
      case ImpactMetric.BUCKETS:
        this.selectedBucketField = data.ratingFieldName;
        this.onBucketFieldChange(this.selectedBucketField, true);
        break;
      case ImpactMetric.RATING:
        // Default behavior - form already initialized with RATING
        break;
    }
  }

  onBucketFieldChange(fieldName: string, _isInit?: boolean) {
    const data = this.data();
    const field = this.bucketFields.find((f) => f.fieldName === fieldName);
    // Check if there are stored custom buckets for this specific field
    const storedBucketData = data.customBucketsByField?.[fieldName];

    // Check if there are custom buckets (either direct or field-specific)
    const hasCustomBuckets = data.customBuckets && data.customBuckets.length > 0;

    //Set to edit mode if we are using custom buckets.
    this.isBucketsReadOnly.set(!hasCustomBuckets);

    // Update override checkbox based on stored data
    if (hasCustomBuckets) {
      this.form.controls.overrideBucketSettings.setValue(true);
      // If we have custom buckets but not stored we need to set selectedBucketIndex to undefined otherwise Apply would be enabled falsly
      this.selectedBucketIndex = storedBucketData?.selectedBucketIndex ?? undefined;
    } else {
      // No custom buckets - use original buckets
      this.form.controls.overrideBucketSettings.setValue(false);
      // if we are not using custom buckets we need to set selectedBucketIndex to undefined
      this.selectedBucketIndex = undefined;
      this.fieldBuckets = undefined;
    }

    // Determine which buckets to use (overriden or original)
    const bucketsToUse =
      hasCustomBuckets && storedBucketData?.buckets ? storedBucketData.buckets : [];

    // Now set the field, which will trigger bucket input change with correct isReadOnly state
    this.field.set({
      ...field,
      buckets: hasCustomBuckets ? bucketsToUse : field.buckets,
    });

    // Emit field change for dynamic updates
    this.fieldChanged.emit({
      field,
      buckets: hasCustomBuckets ? bucketsToUse : field.buckets,
      selectedBucketIndex: storedBucketData?.selectedBucketIndex ?? 0,
      impactMetric: ImpactMetric.BUCKETS,
    });
  }

  onBinaryFieldChange(fieldName: string) {
    const field = this.binaryFields.find((f) => f.fieldName === fieldName);
    this.field.set(field);

    // Emit field change for dynamic updates
    this.fieldChanged.emit({
      field,
      buckets: undefined,
      selectedBucketIndex: undefined,
      impactMetric: ImpactMetric.BINARY,
    });
  }

  onCancel(closeType = ModalCloseType.CANCEL) {
    this.modalClosed.emit(closeType);

    if (this.data()?.resolveFn) {
      this.data().resolveFn(closeType);
    }
  }

  onSave() {
    let field;
    if (this.isBucketView) {
      field = this.bucketFields.find((f) => f.fieldName === this.selectedBucketField);
    } else if (this.form.controls.impactMetric.value === ImpactMetric.BINARY) {
      field = this.binaryFields.find((f) => f.fieldName === this.selectedBinaryField);
    } else {
      field = this.ratingFields.find((f) => f.fieldName === this.selectedField);
    }
    const result: FieldSelectDialogResult = {
      field,
      buckets: this.form.controls.overrideBucketSettings.value
        ? this.fieldBuckets || this.field()?.buckets
        : undefined,
      selectedBucketIndex: this.selectedBucketIndex,
      impactMetric: this.form.controls.impactMetric.value,
    };
    this.modalClosed.emit(result);

    if (this.data()?.resolveFn) {
      this.data().resolveFn(result);
    }
  }

  onBack() {
    if (this.isBucketView) {
      this.isBucketView = false;
      this.widthChange.emit(null);
    } else {
      this.onCancel(ModalCloseType.CANCEL);
    }
  }

  onNext() {
    if (this.isBucketView) {
      this.onSave();
      return;
    }
    this.switchToBucketView();
  }

  private switchToBucketView() {
    this.isBucketView = true;
    this.backButtonText = translate('widgets.options.back');
    this.nextButtonText = translate('widgets.options.apply');
    this.widthChange.emit('800px');
  }

  onFieldBuckets(formValue: FieldBucketsFormValue) {
    this.fieldBuckets = formValue.value;
    this.areBucketsValid = formValue.valid;
  }

  onBucketSelectedIndex(index) {
    this.selectedBucketIndex = index;
  }
}
